import React, { useEffect, useState } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { HaleWordmark } from '../../components/HaleBrand';
import {
  CheckCircle, Clock, Warning, X, Sparkle, ArrowRight,
  ShieldCheck, Leaf, FileText,
} from '@phosphor-icons/react';
import { HALE_BG, HALE_TEXT_MUTED, HALE_TEXT_FAINT } from './_design';
import PortfolioSection from './PortfolioSection';

const API = process.env.REACT_APP_BACKEND_URL;

/* /hale/membership — brand-facing membership workspace.
 *
 * Entry paths:
 *   1. via email magic link: /hale/membership?token=XYZ → POST /verify
 *      → auth cookie set → app fetched
 *   2. already authenticated brand: /hale/membership → GET /me directly
 *
 * Shows: application status, recommended pathway, badges (when active),
 * onboarding checklist, next action.
 */

const STATUS_VISUAL = {
  draft:                            { Icon: Clock,          color: '#9CA3AF', label: 'Draft' },
  pending_review:                   { Icon: Clock,          color: '#E63946', label: 'In review' },
  approved:                         { Icon: CheckCircle,    color: '#10B981', label: 'Approved' },
  approved_with_recommendations:    { Icon: CheckCircle,    color: '#F59E0B', label: 'Approved · with recommendations' },
  pending_revision:                 { Icon: Warning,        color: '#F59E0B', label: 'Pending revision' },
  not_approved:                     { Icon: X,              color: '#9B1C1C', label: 'Not approved' },
};

const TRACK_LABEL = { hale_reviewed: 'Reviewed™', hale_verified: 'Verified™', both: 'Reviewed™ + Verified™' };

export default function HaleMembershipPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [app, setApp] = useState(null);
  const [membership, setMembership] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const token = params.get('token');
      try {
        if (token) {
          await axios.post(`${API}/api/hale-marketplace/applications/verify`, { token },
            { withCredentials: true });
          // Clean the URL so reloads don't re-verify.
          navigate('/hale/membership', { replace: true });
        }
        const [a, m] = await Promise.all([
          axios.get(`${API}/api/hale-marketplace/applications/me`, { withCredentials: true }),
          axios.get(`${API}/api/hale-marketplace/memberships/me`, { withCredentials: true }),
        ]);
        if (cancelled) return;
        setApp(a.data); setMembership(m.data?.membership || null);
      } catch (e) {
        if (e?.response?.status === 401 || e?.response?.status === 404) {
          setError('not_authenticated');
        } else {
          setError(e?.response?.data?.detail || 'Could not load your membership');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-[#0B1426]/50 text-sm"
                 style={{ backgroundColor: HALE_BG }}>Loading your workspace…</div>;
  }

  if (error === 'not_authenticated') {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center text-[#0B1426]"
           style={{ backgroundColor: HALE_BG }}>
        <HaleWordmark size="md" subtitle="Member workspace" />
        <p className="mt-8 text-sm text-[#0B1426]/60 max-w-md text-center">
          This workspace is only available via the magic link emailed to you after submitting an application.
        </p>
        <div className="flex gap-3 mt-8">
          <Link to="/hale/apply">
            <Button className="rounded-full bg-[#E63946] text-black h-10 px-6 text-[11px] uppercase tracking-[0.22em] font-bold">
              Apply now
            </Button>
          </Link>
          <Link to="/login">
            <Button variant="outline" className="rounded-full bg-transparent border-[#0B1426]/20 text-[#0B1426]/70 h-10 px-6 text-[11px] uppercase tracking-[0.22em] font-bold">
              Brand sign in
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  if (!app) return null;

  const status = STATUS_VISUAL[app.status] || STATUS_VISUAL.draft;
  const StatusIcon = status.Icon;
  const rec = app.recommendation || {};
  const draft = app.draft || {};
  const brandName = draft.brand?.brand_name || 'Your brand';
  const founder = draft.founder?.full_name || '';

  return (
    <div className="min-h-screen text-[#0B1426]" style={{ backgroundColor: HALE_BG }}>
      <header className="border-b px-6 lg:px-12 py-5" style={{ borderColor: 'rgba(11,20,38,0.06)' }}>
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <HaleWordmark size="sm" />
          <Link to="/hale/marketplace" className="text-[10px] uppercase tracking-[0.22em] text-[#0B1426]/50 hover:text-[#0B1426]">
            Marketplace
          </Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 lg:px-12 py-16">
        {/* Welcome */}
        <p className="text-[10px] font-bold uppercase tracking-[0.32em] text-[#E63946]">Governance workspace</p>
        <h1 className="text-4xl lg:text-5xl leading-[1.05] tracking-tight font-light mt-3"
            style={{ fontFamily: '"Cormorant Garamond", "Times New Roman", serif' }}>
          Welcome, <em className="not-italic italic">{founder.split(' ')[0] || brandName}</em>.
        </h1>
        <p className="text-base text-[#0B1426]/60 mt-4 max-w-2xl">
          {brandName} · Governance infrastructure activation in progress.
        </p>

        {/* Top status strip */}
        <div className="grid lg:grid-cols-3 gap-5 mt-12">
          <Card label="Onboarding status">
            <div className="flex items-center gap-3">
              <StatusIcon size={20} weight="fill" style={{ color: status.color }} />
              <p className="text-lg font-bold" style={{ color: status.color }} data-testid="member-status">{status.label}</p>
            </div>
          </Card>
          <Card label="Governance pathway">
            <p className="text-lg font-bold text-[#0B1426]" data-testid="member-track">
              {rec.track ? `HALE ${TRACK_LABEL[rec.track]}` : '—'}
            </p>
            <p className="text-[10px] uppercase tracking-[0.22em] text-[#0B1426]/40 mt-1">
              {rec.tier ? `${rec.tier} tier` : '—'}
            </p>
          </Card>
          <Card label="Membership">
            {membership ? (
              <>
                <p className="text-lg font-bold text-[#0B1426]">{membership.status?.replace('_', ' ')}</p>
                <p className="text-[10px] uppercase tracking-[0.22em] text-[#0B1426]/40 mt-1">
                  Renews · {membership.renews_at ? new Date(membership.renews_at).toLocaleDateString() : '—'}
                </p>
              </>
            ) : (
              <p className="text-lg font-bold text-[#0B1426]/40">Awaiting activation</p>
            )}
          </Card>
        </div>

        {/* Decision narrative */}
        {app.decision && (
          <div className="mt-10 rounded-lg p-7"
               style={{ backgroundColor: 'rgba(230,57,70,0.06)', border: '1px solid rgba(230,57,70,0.25)' }}>
            <p className="text-[10px] font-bold uppercase tracking-[0.32em] text-[#E63946] mb-3">
              Reviewer decision · {new Date(app.decision.decided_at).toLocaleDateString()}
            </p>
            <p className="text-base text-[#0B1426]/90 leading-relaxed">{app.decision.reviewer_notes}</p>
            {app.decision.recommendations?.length > 0 && (
              <ul className="mt-5 space-y-2">
                {app.decision.recommendations.map((r, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-[#0B1426]/80">
                    <ArrowRight size={14} className="mt-1 flex-shrink-0" style={{ color: '#E63946' }} />
                    {r}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Badges when active */}
        {membership && membership.badges?.length > 0 && (
          <div className="mt-10">
            <p className="text-[10px] font-bold uppercase tracking-[0.32em] text-[#0B1426]/40 mb-4">Your standards badges</p>
            <div className="flex gap-3 flex-wrap">
              {membership.badges.includes('hale_reviewed') && <BadgeChip type="reviewed" />}
              {membership.badges.includes('hale_verified') && <BadgeChip type="verified" />}
            </div>
          </div>
        )}

        {/* Pathway rationale callout */}
        {rec.rationale && (
          <div className="mt-10 pl-6 border-l-2" style={{ borderColor: '#E63946' }}>
            <p className="text-[10px] font-bold uppercase tracking-[0.32em] text-[#0B1426]/40 mb-2">Concierge note</p>
            <p className="text-[15px] leading-[1.7] text-[#0B1426]/85">{rec.rationale}</p>
          </div>
        )}

        {/* Next steps */}
        <NextSteps app={app} membership={membership} />

        {/* Phase 3 — Marketplace activation + SKU portfolio (only when a membership has been provisioned) */}
        {membership && <PortfolioSection membership={membership} />}
      </main>
    </div>
  );
}

function Card({ label, children }) {
  return (
    <div className="rounded-lg p-6" style={{ backgroundColor: 'rgba(11,20,38,0.03)', border: '1px solid rgba(11,20,38,0.06)' }}>
      <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#0B1426]/40 mb-3">{label}</p>
      {children}
    </div>
  );
}

function BadgeChip({ type }) {
  const config = type === 'reviewed'
    ? { Icon: ShieldCheck, label: 'HALE Reviewed™', sub: 'Scientific governance · retailer readiness' }
    : { Icon: Leaf,        label: 'HALE Verified™', sub: 'Documentation maturity · supplier interoperability' };
  const { Icon } = config;
  return (
    <div className="flex items-center gap-3 px-5 py-4 rounded-lg"
         style={{ backgroundColor: 'rgba(230,57,70,0.08)', border: '1px solid rgba(230,57,70,0.3)' }}
         data-testid={`badge-${type}`}>
      <div className="w-10 h-10 rounded flex items-center justify-center" style={{ backgroundColor: '#E63946' }}>
        <Icon size={20} weight="fill" style={{ color: '#0B0B0B' }} />
      </div>
      <div>
        <p className="text-sm font-bold text-[#0B1426]">{config.label}</p>
        <p className="text-[10px] uppercase tracking-[0.22em] text-[#0B1426]/50">{config.sub}</p>
      </div>
    </div>
  );
}

function NextSteps({ app, membership }) {
  const status = app.status;
  let title = '';
  let steps = [];
  if (status === 'pending_review') {
    title = "What's next";
    steps = [
      'Reviewer assignment within 2 business days',
      'Onboarding call to finalise scope + retailer documentation mapping',
      'Governance infrastructure activation in your workspace',
    ];
  } else if (status === 'approved' || status === 'approved_with_recommendations') {
    title = 'Governance activation underway';
    steps = [
      membership ? 'Confirm membership terms and activation' : 'Membership being provisioned',
      'Upload your initial SKU portfolio',
      'Receive HALE badges for retailer-facing surfaces',
    ];
  } else if (status === 'pending_revision') {
    title = 'Revisions requested';
    steps = ['Address reviewer feedback', 'Resubmit when ready'];
  } else if (status === 'not_approved') {
    title = 'Decision';
    steps = ['Review reviewer notes', 'Reapply with adjusted positioning at any time'];
  }
  if (!steps.length) return null;
  return (
    <div className="mt-12">
      <p className="text-[10px] font-bold uppercase tracking-[0.32em] text-[#0B1426]/40 mb-5">{title}</p>
      <ol className="space-y-3 text-[15px] text-[#0B1426]/85">
        {steps.map((s, i) => (
          <li key={i}><span className="font-mono text-[#0B1426]/30 mr-3 tabular-nums">0{i + 1}</span>{s}</li>
        ))}
      </ol>
    </div>
  );
}
